import { resolveDiscussionAssetUrl } from '../lib/discussionUtils';
import { discussionThreadMediaGallery } from '../lib/discussionMedia';
import { resolveDiscussionCacheTenantId } from '../tenant/discussionTenantScope';

const DB_NAME = 'bahlusDiscussionMediaCache';
const STORE = 'blobs';
const DB_VERSION = 1;
const TTL_MS = 2 * 24 * 60 * 60 * 1000;
const MAX_CACHE_BYTES = 120 * 1024 * 1024;

/** @type {Map<string, { blobUrl: string, size: number }>} */
const memoryBlobUrls = new Map();
/** @type {Map<string, Promise<string>>} */
const inflight = new Map();

function canUseIndexedDb() {
  return typeof window !== 'undefined' && typeof indexedDB !== 'undefined';
}

function openDb() {
  if (!canUseIndexedDb()) return Promise.resolve(null);
  return new Promise((resolve) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'key' });
        store.createIndex('fetchedAt', 'fetchedAt', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}

export function clearDiscussionMediaMemoryCache() {
  for (const key of [...memoryBlobUrls.keys()]) {
    revokeMemoryEntry(key);
  }
  inflight.clear();
}

function uploadPathKey(resolvedUrl) {
  const u = String(resolvedUrl || '').trim();
  if (!u) return '';
  try {
    const parsed = new URL(u, window.location.origin);
    if (!parsed.pathname.startsWith('/uploads/')) return '';
    return parsed.pathname;
  } catch {
    const path = u.split('?')[0].split('#')[0];
    return path.startsWith('/uploads/') ? path : '';
  }
}

/** Stable cache key: tenant + upload pathname (no auth token). */
export function uploadMediaCacheKey(resolvedUrl, tenantId) {
  const path = uploadPathKey(resolvedUrl);
  if (!path) return '';
  const tid = String(tenantId || resolveDiscussionCacheTenantId() || '').trim();
  if (!tid) return '';
  return `${tid}:${path}`;
}

/** Network URL for upload fetch (keeps ?token= for guest viewers; session cookie also works when logged in). */
export function discussionMediaFetchUrl(resolvedUrl) {
  const u = String(resolvedUrl || '').trim();
  if (!u) return '';
  try {
    const parsed = new URL(u, window.location.origin);
    const search = parsed.searchParams.toString();
    return `${parsed.pathname}${search ? `?${search}` : ''}`;
  } catch {
    return u;
  }
}

/** @deprecated use discussionMediaFetchUrl */
export const stableDiscussionFetchUrl = discussionMediaFetchUrl;

function revokeMemoryEntry(key) {
  const entry = memoryBlobUrls.get(key);
  if (!entry) return;
  try {
    URL.revokeObjectURL(entry.blobUrl);
  } catch {
    /* ignore */
  }
  memoryBlobUrls.delete(key);
}

async function readEntry(key) {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => resolve(null);
  });
}

async function deleteEntry(key) {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

async function getTotalCachedBytes(db) {
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readonly');
    const store = tx.objectStore(STORE);
    const req = store.getAll();
    req.onsuccess = () => {
      const rows = Array.isArray(req.result) ? req.result : [];
      resolve(rows.reduce((sum, row) => sum + (Number(row.size) || 0), 0));
    };
    req.onerror = () => resolve(0);
  });
}

async function evictOldestUntilUnder(db, maxBytes, incomingBytes) {
  let total = await getTotalCachedBytes(db);
  if (total + incomingBytes <= maxBytes) return;

  const rows = await new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(Array.isArray(req.result) ? req.result : []);
    req.onerror = () => resolve([]);
  });

  rows.sort((a, b) => (a.fetchedAt || 0) - (b.fetchedAt || 0));
  for (const row of rows) {
    if (total + incomingBytes <= maxBytes) break;
    await deleteEntry(row.key);
    // Do not revoke in-memory blob URLs: feed <img> tags may still point at them.
    total -= Number(row.size) || 0;
  }
}

async function writeEntry(entry) {
  const db = await openDb();
  if (!db) return;
  await evictOldestUntilUnder(db, MAX_CACHE_BYTES, entry.size || 0);
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(entry);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

async function purgeExpiredEntries() {
  const db = await openDb();
  if (!db) return;
  const cutoff = Date.now() - TTL_MS;
  const rows = await new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(Array.isArray(req.result) ? req.result : []);
    req.onerror = () => resolve([]);
  });
  for (const row of rows) {
    if ((row.fetchedAt || 0) < cutoff) {
      await deleteEntry(row.key);
    }
  }
}

let purgeStarted = false;
function schedulePurgeExpired() {
  if (purgeStarted || !canUseIndexedDb()) return;
  purgeStarted = true;
  purgeExpiredEntries().catch(() => {});
}

function rememberBlobUrl(key, blob) {
  const existing = memoryBlobUrls.get(key);
  if (existing?.blobUrl) return existing.blobUrl;
  const blobUrl = URL.createObjectURL(blob);
  memoryBlobUrls.set(key, { blobUrl, size: blob.size || 0 });
  return blobUrl;
}

function isUsableMediaBlob(blob) {
  if (!blob || !blob.size) return false;
  const type = String(blob.type || '').toLowerCase();
  if (!type || type === 'application/octet-stream' || type === 'binary/octet-stream') return true;
  if (type.startsWith('image/') || type.startsWith('video/')) return true;
  return false;
}

/** Sync lookup of an already-created blob URL (no fetch). */
export function peekCachedDiscussionMediaUrl(resolvedUrl) {
  const key = uploadMediaCacheKey(resolvedUrl);
  if (!key) return '';
  return memoryBlobUrls.get(key)?.blobUrl || '';
}

/**
 * Return a blob URL for cached media, or fetch + cache. Falls back to the original URL on failure.
 * @param {string} resolvedUrl already resolved asset URL (may include ?token=)
 */
export async function getOrFetchDiscussionMedia(resolvedUrl) {
  const raw = String(resolvedUrl || '').trim();
  if (!raw) return '';

  const key = uploadMediaCacheKey(raw);
  if (!key) return raw;

  schedulePurgeExpired();

  const mem = memoryBlobUrls.get(key);
  if (mem?.blobUrl) return mem.blobUrl;
  if (inflight.has(key)) return inflight.get(key);

  const task = (async () => {
    const fromMemory = () => memoryBlobUrls.get(key)?.blobUrl || '';
    const hit = fromMemory();
    if (hit) return hit;

    const cached = await readEntry(key);
    const afterRead = fromMemory();
    if (afterRead) return afterRead;

    if (cached?.blob && Date.now() - (cached.fetchedAt || 0) < TTL_MS && isUsableMediaBlob(cached.blob)) {
      return rememberBlobUrl(key, cached.blob);
    }
    if (cached) {
      await deleteEntry(key);
    }

    const fetchUrl = discussionMediaFetchUrl(raw);
    try {
      const res = await fetch(fetchUrl, { credentials: 'include', cache: 'default' });
      if (!res.ok) return raw;
      const blob = await res.blob();
      if (!isUsableMediaBlob(blob)) return raw;
      const afterFetch = fromMemory();
      if (afterFetch) return afterFetch;
      await writeEntry({
        key,
        blob,
        size: blob.size,
        contentType: blob.type || '',
        fetchedAt: Date.now(),
      });
      return rememberBlobUrl(key, blob);
    } catch {
      return raw;
    }
  })();

  inflight.set(key, task);
  try {
    return await task;
  } finally {
    if (inflight.get(key) === task) inflight.delete(key);
  }
}

/** Warm cache for upcoming feed media without blocking render. */
export function prefetchDiscussionMediaUrls(urls, { limit = 16 } = {}) {
  const seen = new Set();
  for (const url of urls || []) {
    if (seen.size >= limit) break;
    const resolved = resolveDiscussionAssetUrl(url);
    const key = uploadMediaCacheKey(resolved);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    getOrFetchDiscussionMedia(resolved).catch(() => {});
  }
}

/** Collect resolved media URLs from a slice of feed threads (for scroll-ahead prefetch). */
export function collectThreadMediaUrls(threads, { start = 0, count = 10 } = {}) {
  const list = Array.isArray(threads) ? threads.slice(start, start + count) : [];
  const urls = [];
  for (const thread of list) {
    const gallery = discussionThreadMediaGallery(thread);
    for (const item of gallery) {
      if (item?.url) urls.push(item.url);
      if (item?.posterUrl) urls.push(item.posterUrl);
    }
    const clubThumb = resolveDiscussionAssetUrl(thread?.clubThumbnailUrl);
    if (clubThumb) urls.push(clubThumb);
  }
  return urls;
}
